import { formatEnumLabel } from "@/lib/formatters";
import type {
  CaseRecord,
  CallRow,
  CaseStatusChainSegment,
  EnumBadgeSemanticTone,
  StructuredOutputEntry,
} from "@/types";

function toneForPaOnSuccessfulCall(paRaw: string): EnumBadgeSemanticTone {
  const p = paRaw.trim().toLowerCase().replace(/\s+/g, "_");
  if (!p) return "success";
  if (p === "approved" || p === "not_required") return "success";
  if (p === "denied" || p === "cancelled") return "failure";
  if (p === "pending") return "warning";
  if (p === "not_submitted_live" || p === "not_submitted_ivr") return "warning";
  return "neutral";
}

function segmentForStructuredOutputEntry(so: StructuredOutputEntry): CaseStatusChainSegment {
  const outputs = (so.outputs || {}) as Record<string, unknown>;
  const pa = String(outputs.pa_status ?? "").trim();
  const fail = String(outputs.fail_reason ?? "").trim();

  if (so.success === "true") {
    if (pa) return { label: formatEnumLabel(pa), tone: toneForPaOnSuccessfulCall(pa) };
    return { label: "Success", tone: "success" };
  }
  if (so.success === "false") {
    if (fail) return { label: formatEnumLabel(fail), tone: "failure" };
    return { label: "Failed", tone: "failure" };
  }
  return { label: "Active", tone: "warning" };
}

/** Aggregate segment when there is no structured_outputs_history (aligned with StatusBadge wording). */
function caseAggregateSegment(c: CaseRecord): CaseStatusChainSegment {
  if (c.success === "true") return { label: "Completed", tone: "success" };
  if (c.success === "false") return { label: "Failed", tone: "failure" };
  const st = c.call_status;
  if (st === "IN_PROGRESS") return { label: "In Progress", tone: "info" };
  if (st === "PENDING_VERIFICATION") return { label: "Pending Verification", tone: "warning" };
  if (st === "PENDING") return { label: "Pending", tone: "muted" };
  if (st) return { label: formatEnumLabel(st), tone: "neutral" };
  return { label: "Unknown", tone: "neutral" };
}

/**
 * Ordered status segments for the case table: one per call attempt, or one aggregate if none.
 */
export function getCaseStatusChainSegments(c: CaseRecord): CaseStatusChainSegment[] {
  const hist = c.structured_outputs_history;
  if (hist && hist.length > 0) {
    return hist.map(segmentForStructuredOutputEntry);
  }
  return [caseAggregateSegment(c)];
}

export function getCaseStatusChainLabels(c: CaseRecord): string[] {
  return getCaseStatusChainSegments(c).map((s) => s.label);
}

/**
 * Backend CSV "retry" row: FAILED and duplicatable, or stuck without any VAPI call id
 * (PENDING/IN_PROGRESS/PENDING_VERIFICATION, no last_vapi_call_id / vapiCallIds) — not in
 * an active batch, and not while deferred outbound is in flight.
 */
export function caseIsDashboardDuplicatableFailed(c: CaseRecord): boolean {
  if (c.in_active_retry_batch === true) return false;
  const st = (c.call_status || "").toUpperCase();
  if (st === "FAILED") return true;
  const hasVapiId = Boolean(
    (c.last_vapi_call_id && String(c.last_vapi_call_id).trim()) ||
      (Array.isArray(c.vapiCallIds) && c.vapiCallIds.some((x) => String(x).trim()))
  );
  if (hasVapiId) return false;
  if (c.outbound_deferred_queued === true) {
    return false;
  }
  return (
    st === "PENDING" || st === "IN_PROGRESS" || st === "PENDING_VERIFICATION"
  );
}

/**
 * Flatten cases into individual call rows (one row per structured_outputs_history entry).
 * Cases with no attempts are still shown as one row (with empty call data).
 */
function scheduledForOnRow(c: CaseRecord, callId: string): string | undefined {
  const at = c.outbound_scheduled_for;
  if (!at || !String(at).trim()) return undefined;
  const s = String(at).trim();
  const hist = c.structured_outputs_history;
  if (!hist || hist.length === 0) return s;
  const last = (c.last_vapi_call_id && String(c.last_vapi_call_id).trim()) || "";
  if (last && callId && callId === last) return s;
  return undefined;
}

export function flattenCasesToCallRows(cases: CaseRecord[]): CallRow[] {
  const rows: CallRow[] = [];

  for (const c of cases) {
    const firstName = c.member_first_name || c["Member First Name"];
    const lastName = c.member_last_name || c["Member Last Name"];
    const memberId = c.member_id || c["Member ID"];
    const payerName = c.payer_name || c["Payer Name"];
    const productName =
      c.product_name || c["Product Name #1 (Human)"] || c["Product Name #1"];

    if (
      c.structured_outputs_history &&
      c.structured_outputs_history.length > 0
    ) {
      c.structured_outputs_history.forEach((so, idx) => {
        const outputs = (so.outputs || {}) as Record<string, unknown>;
        rows.push({
          rowKey: `${c.id}__${idx}`,
          caseId: c.id,
          callId: so.call_id,
          attemptIndex: idx,
          totalAttempts: c.structured_outputs_history.length,
          timestamp: so.timestamp,
          phoneNumber: so.phone_number,
          success: so.success,
          paStatus: outputs.pa_status as string | undefined,
          failReason: outputs.fail_reason as string | undefined,
          failDetails: outputs.fail_details as string | undefined,
          paDetails: outputs.pa_details as string | undefined,
          referenceNumber: outputs.reference_number as string | undefined,
          duration: outputs.call_duration as number | undefined,
          scheduledFor: scheduledForOnRow(c, so.call_id),
          memberFirstName: firstName,
          memberLastName: lastName,
          memberId: memberId,
          memberDob: c.member_dob,
          payerName: payerName,
          productName: productName,
          case: c,
        });
      });
    } else {
      // Case with no call history — show as a single row
      rows.push({
        rowKey: `${c.id}__0`,
        caseId: c.id,
        callId: "",
        attemptIndex: 0,
        totalAttempts: 0,
        timestamp: c.created_at,
        phoneNumber: c.payer_phone_number || c["Payer Phone Number"] || "",
        success: c.success,
        paStatus: c.pa_status,
        failReason: undefined,
        failDetails: c.call_details,
        paDetails: c.pa_details,
        referenceNumber: undefined,
        duration: c.call_duration,
        scheduledFor: scheduledForOnRow(c, ""),
        memberFirstName: firstName,
        memberLastName: lastName,
        memberId: memberId,
        memberDob: c.member_dob,
        payerName: payerName,
        productName: productName,
        case: c,
      });
    }
  }

  return rows;
}

/**
 * Filter loaded cases by search term (client-side).
 * Searches across: case ID, member name, member ID, call IDs.
 */
export function filterLoadedCases(
  cases: CaseRecord[],
  searchTerm: string
): CaseRecord[] {
  if (!searchTerm.trim()) return cases;
  const term = searchTerm.toLowerCase().trim();

  return cases.filter((c) => {
    const firstName = (
      c.member_first_name || c["Member First Name"] || ""
    ).toLowerCase();
    const lastName = (
      c.member_last_name || c["Member Last Name"] || ""
    ).toLowerCase();
    const memberId = (c.member_id || c["Member ID"] || "").toLowerCase();
    const caseId = c.id.toLowerCase();
    const callIds = (c.vapiCallIds || []).join(" ").toLowerCase();
    const payerName = (c.payer_name || c["Payer Name"] || "").toLowerCase();
    const productName = (
      c.product_name || c["Product Name #1 (Human)"] || c["Product Name #1"] || ""
    ).toLowerCase();
    const scheduled = (c.outbound_scheduled_for && String(c.outbound_scheduled_for)) || "";

    return (
      caseId.includes(term) ||
      firstName.includes(term) ||
      lastName.includes(term) ||
      `${firstName} ${lastName}`.includes(term) ||
      memberId.includes(term) ||
      callIds.includes(term) ||
      payerName.includes(term) ||
      productName.includes(term) ||
      scheduled.toLowerCase().includes(term)
    );
  });
}

/**
 * Filter call rows by view filter (client-side on flattened rows).
 */
export function filterCallRows(
  rows: CallRow[],
  filter: "all" | "active" | "successful" | "pending_verification" | "failed"
): CallRow[] {
  // NOTE: The backend API already applies the primary filter when fetching.
  // Client-side filtering here handles additional refinement or tabs that
  // share the same API dataset (e.g. "failed" is fetched via "all").
  switch (filter) {
    case "active":
      return rows.filter(
        (r) => r.success !== "true" && r.success !== "false"
      );
    case "successful":
      return rows.filter((r) => r.success === "true");
    case "failed":
      return rows.filter((r) => caseIsDashboardDuplicatableFailed(r.case));
    case "pending_verification":
      // Backend already returns only pending cases — no extra filtering needed
      return rows;
    case "all":
    default:
      return rows;
  }
}

/**
 * Filter call rows by search term (client-side).
 * Searches across: case ID, call ID, member name, member ID, phone, payer, product.
 */
export function filterCallRowsBySearch(
  rows: CallRow[],
  searchTerm: string
): CallRow[] {
  if (!searchTerm.trim()) return rows;
  const term = searchTerm.toLowerCase().trim();

  return rows.filter((r) => {
    const name = `${r.memberFirstName || ""} ${r.memberLastName || ""}`.toLowerCase();
    return (
      r.caseId.toLowerCase().includes(term) ||
      (r.callId || "").toLowerCase().includes(term) ||
      name.includes(term) ||
      (r.memberId || "").toLowerCase().includes(term) ||
      (r.phoneNumber || "").toLowerCase().includes(term) ||
      (r.payerName || "").toLowerCase().includes(term) ||
      (r.productName || "").toLowerCase().includes(term) ||
      (r.paStatus || "").toLowerCase().includes(term) ||
      (r.failReason || "").toLowerCase().includes(term) ||
      (r.scheduledFor || "").toLowerCase().includes(term)
    );
  });
}

